import React, { useState, useContext, useReducer, useEffect, lazy, Suspense } from 'react';
import { AuthContext } from "../contexts/Authentication/AuthStateProvider";
import PreLoadSpinner from "./PreLoadSpinner";
import PrivateRoute from "../routes/PrivateRoute";
import PublicRoute from "../routes/PublicRoute";
import { Route, Switch } from 'react-router-dom';
import SideMenu from './SideMenu/SideMenu';
import Header from './Header/Header';
// import HealthJourneyRouter from './HealthJourney/HealthJourneyRouter';
import useModal from "../hooks/useModal";
import UserProfile from './UserProfile/UserProfile';
import HelpRouter from "./Help/HelpRouter";
import ForumRouter from "./Forums/ForumRouter";
import TrainingRouter from './TrainingPrograms/Route';
//import ChatBot from "../components/Chats/ChatBot"
import Footer from "./Footer/Footer";
import ChatModal from "./Chats/ChatModal";
import { PopupNotification } from "./Chats/useSocket";
import { CTTooltip } from "./Common/CueTooltip";
import DigitalContentRouter from './DigitalContent/DigitalContentRouter';
import LocaleList from "./Locale/LocaleMenu";
import GenericModal from "./Modals/GenericModal";
import PreScheduleEventRouter from './PreScheduleEvent/PreScheduleEventRouter';
import Organization from './Organizations/Route';
import OrganizationForm from './Organizations/OrganizationForm';
import OrganizationsModal from "./Organizations/OrganizationsModal";
import SubscriptionsRouter from './Subscriptions/Route';
import WelcomeTour from './DigitalContent/WelcomeTour';
import ContactsRouter from './Contacts/ContactsRouter';
import { LocalizeContext } from "../contexts/Localize/LocalizeStateProvider";
import LocaleReducer from '../contexts/Localize/LocalizeReducer';
import { AboutUs } from './AboutUs/AboutUs';
import { TermsPrivacyConsent } from './AboutUs/TermsPrivacyConsent';
import ErrorBoundary from "./Common/ErrorBoundary";
import NoteForm from "./FormBuilder/FormFieldNotes/NoteForm";
import querystringify from "querystringify";
import { PushNotification } from "./PushNotification/PushNotificationPage";
import { useSubscriptionAccess } from './Subscriptions/useSubscription';
import FormTestInputRoute from './FormTestInput/FormTestInputRoute';
import CookieNotice from "./Common/CookieNotice";
import GeographyRoute from "./Geography/GeographyRoute";
import { UpdateBreakPoints } from "../hooks/useScreenViewport";
import { Spinner } from './Common/Spinner';
import EventRoute from './Events/EventRoute';
import UserRegistrationRoute from './UserRegistrations/UserRegistrationRoute';
import JourneyProfileRoute from './JourneyProfiles/JourneyProfileRoute';
import AlumniRouter from './Organizations/Alumni/AlumniRouter';
import FacultyRouter from './Organizations/Faculties/FacultyRouter';
import MemberPortalRouter from './Organizations/MemberPortal/MemberPortalRoute';
import CourseCompletionsRouter from './CourseCompletions/CourseCompletionsRouter';
import ProductFeatureRouter from './ProductFeatures/ProductFeatureRouter';
import BlogRouter from './Blogs/BlogRoute';
import SuppliersRouter from './Suppliers/SuppliersRouter';
import CallForProposalsRouter from './CallForProposals/CallForProposalsRouter';
import PartnerRouter from './Global/Partners/PartnerRouter';
//import { Validation } from './FormBuilder/ZodValidation';

const ImageCompress = lazy(() => import('./ImageEditor/ImageCompress'));
const FeedbackForm = lazy(() => import('./Feedback/FeedbackForm'));
const FeedbackList = lazy(() => import('./Feedback/List/FeedbackList'));
const UserHelpRequest = lazy(() => import('./Feedback/List/UserHelpRequest'));
const DataSourceList = lazy(() => import('./DataSources/DataSourceList'));
const FormsList = lazy(() => import('./DataSources/FormsList'));
const FormSuggestion = lazy(() => import('./DataSources/FormSuggestion'));
// const LeaderShipDashboard = lazy(() => import("./HealthJourney/Dashboard/LeaderShipDashboard"));
const NotificationList = lazy(() => import('./Notifications/NotificationList'));
const NotificationTabView = lazy(() => import('./Notifications/NotificationTabView'));
const PublicNotificationsPage = lazy(() => import('./Notifications/PublicNotificationsPage'));
const ContentManager = lazy(() => import('./Dashboard/ContentManager'));
const LoginHistory = lazy(() => import('./Authentication/LoginHistory'));
const Form = lazy(() => import('./FormBuilder/Form'));
const FormImageSelect = lazy(() => import('./FormBuilder/FormImageSelect'));
const JourneyLogs = lazy(() => import('./ApiLogs/JourneyLogs'));
const DataEntryMemberList = lazy(() => import("./DataEntryUser/DataEntryMemberList"));
const FormSuggestionsList  = lazy(() => import('./DataSources/FormSuggestionsList'));
const FormLabelList = lazy(() => import('./DataSources/FormLabelList'));
const ImageExif = lazy(() => import('./ImageEditor/ImageExif')); 
const AIPromptListTable = lazy(() => import('./AIPrompts/AIPromptList'));  
const RoleList = lazy(() => import('./Roles/RoleList'));

const Home = (props) => {
  const queryParam = querystringify.parse(props.location.search);

  const { state:{screen, user:currentUser, isAuthenticated:isAuthenticated}, dispatch } = useContext(AuthContext);
  const [localeState, localeDispatch] = useReducer(LocaleReducer, {});

  let [openSideNav, setOpenSideNav] = useState(false);
  let [searchString, setSearchString] = useState(queryParam.search);
  let [openChatBot, setOpenChatBot] = useState(false);

  const {isOpen: showChatModal, toggleModal: toggleChatModal} = useModal();
  const {isOpen: showLocaleModal, toggleModal: toggleLocaleModal} = useModal();
  const {isOpen: showOrgModal, toggleModal: toggleOrgModal} = useModal();

  const {getSubscriptionAccess, loading:subscriptionLoading} = useSubscriptionAccess();
  UpdateBreakPoints();

  useEffect(()=>{
    getSubscriptionAccess()
  }, [currentUser.current_organization_id, currentUser.current_locale])

  if(subscriptionLoading){
    return(
      <PreLoadSpinner/>
    )
  }

  return (
    <>
      <LocalizeContext.Provider value={{localeState, localeDispatch}}>
          {isAuthenticated && 
            <Header openSideNav={openSideNav} setOpenSideNav={setOpenSideNav} 
            setSearchString={setSearchString} searchString={searchString}
            toggleLocaleModal={toggleLocaleModal} queryParam={queryParam}
            toggleOrgModal={toggleOrgModal} {...props}/>
          }
          
          <ChatModal isOpen={showChatModal} toggleModal={toggleChatModal} currentUser={currentUser}/>
        
          {/*screen.xs && <AddToHomeScreen/>*/}

          {isAuthenticated && 
            <SideMenu openSideNav={openSideNav} setOpenSideNav={setOpenSideNav} 
            toggleOrgModal={toggleOrgModal} toggleChatModal={toggleChatModal}
            toggleLocaleModal={toggleLocaleModal}/>
          }

          {showLocaleModal && 
            <GenericModal component={LocaleList} 
            title="Language" type="user_locale"
            selectedItems={[currentUser.current_locale]}
            isOpen={showLocaleModal} toggleModal={toggleLocaleModal}/>
          }

          {showOrgModal && 
            <GenericModal component={OrganizationsModal} 
            title="Select Organization"
            isOpen={showOrgModal} toggleModal={toggleOrgModal}/>
          }

          <PopupNotification currentUser={currentUser}/>

          <ErrorBoundary>
            <Switch> 
              <Route path="/profile/:id?" component={OrganizationForm} exact />
              <PrivateRoute path="/user/profile" component={UserProfile} exact />
              <PrivateRoute path="/add/formfield/notes/:annotation_form_id/:id?" component={NoteForm} exact/>
              <Suspense fallback={<Spinner/>}>
                <PrivateRoute path="/image/test" component={ImageCompress} exact />
                <PrivateRoute path="/image/data" component={ImageExif} exact />
                <PrivateRoute path="/feedback/:id?" component={FeedbackForm} exact/>
                <PrivateRoute path="/list/feedback" component={FeedbackList} searchString={searchString} exact/>
                <PrivateRoute path="/user/feedbacks" component={UserHelpRequest} exact/>
                <PrivateRoute path="/data/entry/members" component={DataEntryMemberList} exact />
                <PrivateRoute path="/notifications" component={NotificationTabView} searchString={searchString} exact />
                <PrivateRoute path="/case/logs/:journey_profile_id" component={JourneyLogs} exact/>
                <PrivateRoute path="/list/formfield/images" component={FormSuggestionsList} exact/>
                <PrivateRoute path="/form/images/:form_template_id" component={FormImageSelect} exact />
                <PrivateRoute path="/form/:form_template_id" component={Form} exact/>
                {/* <PrivateRoute path="/leadership" component={LeaderShipDashboard} exact /> */}
                <PrivateRoute path="/form/feedback/:form_id" component={FormSuggestion} exact />
                <PrivateRoute path="/list/data_sources" component={DataSourceList} searchString={searchString} exact />
                <PrivateRoute path="/list/forms" component={FormsList} searchString={searchString} exact />
                <PrivateRoute path="/form/locale/suggestions" component={FormsList} searchString={searchString} exact />
                <PrivateRoute path="/language/labels" component={FormLabelList} searchString={searchString} exact />
                <PrivateRoute path="/login/historys" component={LoginHistory} exact />
                <PrivateRoute path="/manage/contents" component={ContentManager} exact />
                <PrivateRoute path="/ai/prompts" component={AIPromptListTable} searchString={searchString} exact/>
                <PrivateRoute path="/ecn/role/:roleable_type/:roleable_id/:organization_id/:subscription_id" searchString={props.searchString} component={RoleList} exact />
                <PrivateRoute path="/ecn/role/templates/:subscription_id" searchString={props.searchString} component={RoleList} exact />
                <PublicRoute path="/public/notifications/:journey_profile_id/:locale_sid/:parent_notification_id?" 
                component={PublicNotificationsPage} exact />
                {/*<PublicRoute path="/zod/validation" component={Validation} exact/>*/}
              </Suspense>
            </Switch>
            {/* <Switch><HealthJourneyRouter searchString={searchString}/></Switch> */}
            <Switch><ContactsRouter searchString={searchString}/></Switch>
            <Switch><ProductFeatureRouter searchString={searchString}/></Switch>
            <Switch><CourseCompletionsRouter searchString={searchString}/></Switch>
            <Switch><BlogRouter searchString={searchString}/></Switch>
            <Switch><SuppliersRouter searchString={searchString}/></Switch>
            <Switch><CallForProposalsRouter searchString={searchString}/></Switch>
            <Switch><PartnerRouter searchString={searchString}/></Switch>
            <Switch><AlumniRouter searchString={searchString}/></Switch>
            <Switch><FacultyRouter searchString={searchString}/></Switch>
            <Switch><MemberPortalRouter searchString={searchString}/></Switch>
            <Switch><HelpRouter searchString={searchString} /></Switch>
            <Switch><ForumRouter /></Switch>
            <Switch><TrainingRouter searchString={searchString}/></Switch>
            <Switch><PreScheduleEventRouter searchString={searchString}/></Switch>
            <Switch><DigitalContentRouter searchString={searchString} /></Switch>
            <Switch><Organization searchString={searchString} /></Switch>
            <Switch><SubscriptionsRouter searchString={searchString}/></Switch>
            <Switch><FormTestInputRoute searchString={searchString}/></Switch>
            <Switch><GeographyRoute searchString={searchString}/></Switch>
            <Switch><EventRoute searchString={searchString}/></Switch>
            <Switch><UserRegistrationRoute searchString={searchString}/></Switch>
            <Switch><JourneyProfileRoute searchString={searchString}/></Switch>
            {isAuthenticated && <Footer openChatBot={openChatBot} setOpenChatBot={setOpenChatBot} />}
            
            <PrivateRoute path="/journey/tour" component={WelcomeTour} exact />
            <PublicRoute path="/about_us/:content_type" component={AboutUs} exact/>
            <PublicRoute path="/organization/consent" component={TermsPrivacyConsent} exact/>
            <PublicRoute path="/push/settings" component={PushNotification} exact/>
            <CTTooltip />
            {localStorage?.is_cookie_accepted === undefined && <CookieNotice />}
          </ErrorBoundary>
      </LocalizeContext.Provider>
    </>
  )
}

export default Home;